import { SearchIcon } from '@heroicons/react/outline';
import clsx, { ClassValue } from 'clsx';
import Button from 'components/Button';
import { FC, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

interface ITableSearch {
  totalCount: number;
  search: string;
  onSearch: (search: string) => void;
  className?: ClassValue;
  theme?: 'default' | 'version-1';
}

const themes = {
  default: {
    searchWrapper: '',
    input: 'rounded-md border border-gray-400/30 font-Sohne-Light'
  },
  'version-1': {
    searchWrapper: 'justify-end w-full mr-5',
    input: 'rounded-l-lg'
  }
};

const TableSearch: FC<ITableSearch> = ({
  totalCount,
  search,
  onSearch,
  className,
  theme = 'default'
}) => {
  const [value, setValue] = useState<string>(search);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  const handleDebounceSearch = useDebouncedCallback((value: string) => {
    onSearch(value);
  }, 1000);

  useEffect(() => {
    if (theme === 'default') {
      setShowSearch(true);
    }
  }, [theme]);

  return (
    <div className={clsx('space-x-3 flex items-center', className, themes[theme].searchWrapper)}>
      {theme === 'default'}
      <div className="flex items-center w-full">
        {showSearch && (
          <div className="relative w-full">
            <input
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                handleDebounceSearch(e.target.value);
              }}
              placeholder={`Search ${totalCount} results..`}
              className={clsx(
                'text-xs p-2.5 pr-9 w-full',
                'focus:outline-none focus:ring-0 focus:border-gray-200',
                themes[theme].input
              )}
              autoFocus={showSearch}
            />

            <SearchIcon className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        )}

        {theme !== 'default' && (
          <Button
            variant="invisible"
            size="inline"
            className="bg-hazel-light-green-600"
            classNameButton="py-2 px-4"
            onClick={() => setShowSearch(!showSearch)}
          >
            <SearchIcon className="text-white h-5 w-5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default TableSearch;
